'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileOrTabletClient } from '@/hooks/common/use-device-type'
import { useSaveVoucher } from '@/hooks/react-query/account/use-save-voucher.mutation'
import { useIsLoggedIn } from '@/hooks/react-query/user/use-user'
import { STORAGE_KEYS } from '@/lib/constants/storage-keys'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { SessionStorage } from '@/lib/utils/session-storage'
import { TCollectionDetail, THighlightVoucherItem } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'

interface HighlightVoucherCardProps {
  item: THighlightVoucherItem
  scrollTargetSelector?: string
}

const HighlightVoucherCard = ({
  item,
  scrollTargetSelector = '#collection-listing',
}: HighlightVoucherCardProps) => {
  const t = useTranslations()
  const isLoggedIn = useIsLoggedIn()
  const [isSaved, setIsSaved] = useState(false)
  const { mutateAsync: saveVoucher, isPending } = useSaveVoucher()

  const handleSaveVoucher = useCallback(
    async (code: string) => {
      try {
        if (isLoggedIn) {
          // Save to backend for logged-in users
          await saveVoucher(code)
          toast.message({
            title: t('title.voucher'),
            message: 'Mã giảm giá đã được lưu vào ví của bạn',
          })
          setIsSaved(true)
        } else {
          // Save to sessionStorage for non-logged-in users
          const saved = SessionStorage.set(STORAGE_KEYS.PENDING_VOUCHER, code)
          if (saved) {
            toast.message({
              title: 'Mã giảm giá đã được lưu tạm',
              message:
                'Đăng nhập để lưu mã vào ví hoặc sử dụng ngay trong giỏ hàng',
            })
            setIsSaved(true)
          } else {
            throw new Error('Không thể lưu mã giảm giá')
          }
        }
      } catch (error: unknown) {
        // Check if it's the "already saved" error from API response
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          (error as Error)?.message ||
          'Có lỗi xảy ra khi lưu mã giảm giá'

        if (errorMessage.includes('đã lưu mã giảm giá này rồi')) {
          toast.message({
            title: 'Thông báo',
            message: 'Mã giảm giá này đã được lưu trong ví của bạn rồi',
          })
          setIsSaved(true)
        } else {
          toast.message({
            title: t('title.voucher'),
            message: errorMessage,
          })
        }
      }
    },
    [isLoggedIn, saveVoucher, t]
  )

  const handleClick = () => {
    if (item.type === 'scroll_to_listing') {
      if (item.link) {
        console.log('item.link', item.link)
        if (item.link.startsWith('#')) {
          const element = document.querySelector(item.link)
          element?.scrollIntoView({ behavior: 'smooth' })
        } else {
          window.location.href = item.link
        }
        return
      }
      // Scroll to listing section
      const element = document.querySelector(scrollTargetSelector)
      element?.scrollIntoView({ behavior: 'smooth' })
      return
    }

    // Handle voucher type
    if (isSaved) {
      // If already saved, scroll to products section
      const element = document.querySelector(scrollTargetSelector)
      element?.scrollIntoView({ behavior: 'smooth' })
      return
    }

    // Save voucher
    handleSaveVoucher(item.voucher)
  }

  const getButtonText = () => {
    if (item.type === 'scroll_to_listing') {
      return t('flash_sale_hardcode.voucher.buy_now') || 'Mua ngay'
    }

    if (isPending) {
      return t('flash_sale_hardcode.voucher.saving') || 'Đang lưu...'
    }

    if (isSaved) {
      return t('flash_sale_hardcode.voucher.buy_now') || 'Mua ngay'
    }

    return t('flash_sale_hardcode.voucher.save_code') || 'Lưu mã'
  }

  return (
    <div
      className={cn(
        'relative flex h-full max-w-96 flex-col justify-end rounded-2xl',
        'aspect-[423/210] overflow-hidden'
      )}
      style={{
        backgroundImage: item.image
          ? `url(${ImageHelper.getImageUrlCdn(item.image)})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      onClick={handleClick}
      ga-tracking-value={`voucher_new-year-campaign_${item.voucher}`}
      ga-tracking-label='Voucher New Year Campaign'
    >
      {/* Button container */}
      {!item.is_hide_cta ? (
        <div className='relative z-10 flex justify-end px-4 pb-2 pt-2'>
          <Button
            variant='white'
            className='h-7 w-fit shrink-0 justify-center whitespace-nowrap bg-white text-xs md:h-[33px] md:text-sm'
            size='small'
            onClick={handleClick}
            disabled={isPending}
            ga-tracking-value={`voucher_new-year-campaign_${item.voucher}`}
            ga-tracking-label='Voucher New Year Campaign'
          >
            {getButtonText()}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

interface HighlightVoucherProps {
  collection: TCollectionDetail
  scrollTargetSelector?: string
}

export const HighlightVoucher = ({
  collection,
  scrollTargetSelector = '#collection-listing',
}: HighlightVoucherProps) => {
  const highlightVouchers = collection.highlight_vouchers
  const isMobileOrTablet = useIsMobileOrTabletClient()

  if (!highlightVouchers || highlightVouchers.length === 0) {
    return null
  }

  return (
    <CollectionContainer className='py-6 md:py-8'>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
          slidesToScroll: 1,
        }}
        className='w-full'
      >
        <CarouselContent
          className={cn(
            '-ml-4',
            !isMobileOrTablet && highlightVouchers.length <= 4
              ? 'md:justify-center'
              : ''
          )}
        >
          {highlightVouchers.map((item, index) => (
            <CarouselItem
              key={`${item.voucher}-${index}`}
              className='basis-4/5 pl-4 pr-1 md:basis-1/4 2xl:basis-1/6'
            >
              <HighlightVoucherCard
                item={item}
                scrollTargetSelector={item?.link || scrollTargetSelector}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselNavigation />
      </Carousel>
    </CollectionContainer>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-7 w-7 -translate-y-full lg:-left-10'
        variant='black'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-7 w-7 -translate-y-full lg:-right-10'
        variant='black'
        hideIfEnd
      />
    </>
  )
}
